import React, { useEffect, useMemo, useState } from 'react';
import './index.scss';
import { getOverviewPcConsoleTimeline, getStudioGameStore } from '@op/services';
import { arrayToObj, arrayToSimpleObj } from '@op/utils';
import { dealwithPcConsoleTimeline } from '@op/pages/gamestore/utils';
import usePcConsoleTimelineChart from '@op/pages/gamestore/hooks/usePcConsoleTimelineChart';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import TrendCard from '../../TrendCard';
import MentionsCharts from '../MentionsCharts';
import ReviewsProgress from '../ReviewsProgress';
import { canRequest } from '../../utils';
import GameStoreTrendCard from './component/GameStoreTrendCard';
const seriesDataKeys = [
  {
    text: 'Positive',
    key: 'select_recommend',
    type: 'bar',
    beginColor: '#3286F3',
    endColor: '#4BD19C',
    valueKey: 'review',
  },
  {
    text: '30-Day Positive %',
    key: 'recent_reviews',
    type: 'line',
    color: '#5086F3',
    valueKey: 'score',
  },
  {
    text: 'Negative',
    key: 'select_non_recommend',
    type: 'bar',
    beginColor: '#AD53D4',
    endColor: '#F17C86',
    valueKey: 'review',
  },
];

type SteamItemType = { review: number; score: number; };
const getDefaultReview = () => ({ review: 0, score: 0 });

interface TrendCardMetricConfig {
  metricKey: string;
  titleEn: string;
  titleCn: string;
  color: string;
  isPercent?: boolean;
}
interface TrendSeriesConfig {
  text: string;
  key: string;
  type: 'line' | 'bar';
  color: string;
  valueKey: string;
  isPercent?: boolean;
}
interface TrendConfigItem {
  metricKey: string;
  reqMetricKey: string;
  isCompare: boolean;
  nameEn: string;
  nameCn: string;
  compareToolTipNameEn: string;
  compareToolTipNameCn: string;
  seriesConfig: TrendSeriesConfig[];
}
interface TrendConfig {
  [channel: string]: TrendConfigItem;
}
interface TrendCardConfig {
  [channel: string]: TrendCardMetricConfig[];
}
interface IProps {
  filter: any;
  isDelay?: boolean;
  isFullscreen?: boolean;
  trendCardConfig?: TrendCardConfig;
  trendConfig?: TrendConfig;
  className?: string;
  style?: React.CSSProperties;
  channel: string[];
}

export default function GameStoreV2(props: IProps) {

  const {filter, isDelay, isFullscreen, trendCardConfig,trendConfig, channel, className, style } = props || {};

  const [steamInfo, setSteamInfo] = useState<{
    all_reviews: SteamItemType;
    recent_reviews: SteamItemType;
  }>({
    all_reviews: getDefaultReview(),
    recent_reviews: getDefaultReview(),
  });

  const [trendTimeline, setTrendsTimeline] = useState({
    list: [],
    timeline_of_end_time: '',
    timeline_of_start_time: '',
  });

  const [cardData, setCardData] = useState({});
  const [trendData, setTrendData] = useState({});
  const [loading, setLoading] = useState(false);
  const [trendCardLoading, setTrendCardLoading] = useState(false);
  const [empty, setEmpty] = useState(true);

  // 获取GameStore数据
  function getPcGameStoreData() {
    setLoading(true);
    getStudioGameStore(filter)
      .then((data: any) => {
        const { list, timeline_of_end_time, timeline_of_start_time, timeline } =
          data || {};
        const gameStoreMap = arrayToSimpleObj(list, 'list', 'channel');
        const localTrendsTimeline: any = dealwithPcConsoleTimeline(
          [{ timeline, channel: 'steam' }],
          timeline_of_start_time,
          timeline_of_end_time,
        );
        setTrendsTimeline(localTrendsTimeline.steam);
        const { steam, ...otherInfo } = gameStoreMap || {};
        setCardData(otherInfo);
        setSteamInfo(arrayToObj(steam, 'key'));
      }).catch(() => {}).finally(() => {
        setLoading(false);
      });
  }
  // 获取卡片趋势图数据
  function getTrendCardData() {
    setTrendCardLoading(true);
    getOverviewPcConsoleTimeline({
      ...filter,
      channels: channel,
      channel,
      metrics: ['recommend', 'non_recommend'],
      ret_metrics: ['recommend', 'non_recommend'],
      types: ['total', 'slice'],
    })
      .then((data: any) => {
        setTrendData(data);
      }).catch(() => {}).finally(() => {
        setTrendCardLoading(false);
      });
  }

  useEffect(() => {
    if (canRequest(filter)) {
      getPcGameStoreData();
      getTrendCardData();
    }
  }, [filter]);

  const { chartMap } = usePcConsoleTimelineChart({
    platformData: trendTimeline,
    filterParams: filter,
  });

  const steamInfoEmpty = useMemo(() => {
    if (!steamInfo) return true;
    const { all_reviews, recent_reviews } = steamInfo;
    if (!all_reviews && !recent_reviews) return true;
    if (
      all_reviews?.review === 0 &&
      recent_reviews?.review === 0 &&
      all_reviews?.score === 0 &&
      recent_reviews?.score === 0
    ) {
      return true;
    }
    return seriesDataKeys.every(({ key, valueKey }) => {
      const data = chartMap[key];
      if (!data || data?.length === 0) return true;
      return data.every((item) => item?.[valueKey] === 0);
    });
  }, [steamInfo]);

  // 判断是否无数据
  useEffect(() => {
    setEmpty(steamInfoEmpty);

  }, [ steamInfoEmpty ]);
  

  return (
    <div className={className} style={style}>
      <TrendCard
        loading={loading || trendCardLoading}
        loadingHeight={450}
        moduleType="game_store"
        filter={filter}
        empty={empty}
        emptyHeight={450}
        hasEmptyDesc={false}
        formatValue={(value: string | number) => dataFormat({ value, formatRule: 'bmk' })}
        className="dbh-trend-card-game-store-V2"
        title={'Game Store'}
        isDelay={isDelay}
        urlKey={'Game Store'}
      >
        <div className="dbh-game-store-v2-reviews-progress">
          <div className="dbh-game-store-v2-reviews-progress-item">
            <ReviewsProgress
              data={steamInfo.all_reviews || getDefaultReview()}
              title="All Reviews"
            />
          </div>
          <div className="dbh-game-store-v2-reviews-progress-item">
            <ReviewsProgress
              data={steamInfo.recent_reviews || getDefaultReview()}
              title="Recent Reviews"
            />
          </div>
        </div>
        <MentionsCharts
          chartMap={chartMap}
          filter={filter}
          seriesDataKeys={seriesDataKeys}
          isFullscreen={isFullscreen}
          height={190}
        />
        <div  className={'dbh-game-store-v2-trend-card-wrap'}>
          {trendCardConfig &&
          Object.entries(trendCardConfig).map(([channel, metrics]) => (
            <GameStoreTrendCard
              key={channel}
              channel={channel}
              className={'dbh-game-store-v2-trend-card'}
              metrics={metrics}
              cardData={cardData}
              trendData={trendData}
              trendConfig={trendConfig}
              filter={filter}
            />
          ))}
        </div>
      </TrendCard>
    </div>
  );
}
